/**
 * <bb-search-predictive data-url="..." data-section="bb-predictive-search"> — predictive search dropdown for the
 * search results page (bb-search section).
 *
 * The element wraps the search form plus an (initially hidden) [data-bb-ps-panel] and a [data-bb-ps-status] live
 * region. While the user types it fetches the `bb-predictive-search` section through the Section Rendering API,
 * takes its [data-bb-ps-root] element and moves it into the panel. Markup always comes from Shopify's rendered
 * section (escaped server side); nothing here builds HTML from the query string.
 *
 * Accessibility: ARIA 1.2 combobox on the input (aria-controls / aria-expanded / aria-activedescendant) with a
 * listbox of [role=option] links. The options never take focus; DOM focus stays in the input and the "active"
 * option is exposed through aria-activedescendant. Without JS the form is a plain GET search form.
 *
 * Keyboard: ArrowDown / ArrowUp move through the options (wrapping), Enter follows the active option (otherwise
 * the form submits normally), Escape closes, Tab closes. Click/tap outside closes.
 */
const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const CACHE_LIMIT = 30;

class BBSearchPredictive extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #status = null;
  /** @type {number} */
  #timer = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Map<string, string>} lowercase query -> section HTML */
  #cache = new Map();
  /** @type {string} lowercase query the panel content was rendered for */
  #renderedKey = '';
  #activeIndex = -1;
  #open = false;

  connectedCallback() {
    const input = this.querySelector('input[type="search"][name="q"]') ?? this.querySelector('input[name="q"]');
    const panel = this.querySelector('[data-bb-ps-panel]');
    if (!(input instanceof HTMLInputElement) || !(panel instanceof HTMLElement)) return;

    this.#input = input;
    this.#panel = panel;
    this.#status = this.querySelector('[data-bb-ps-status]');

    if (!panel.id) panel.id = `bb-ps-panel-${Math.random().toString(36).slice(2, 8)}`;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-controls', panel.id);
    input.setAttribute('aria-expanded', 'false');

    input.addEventListener('input', this.#onInput);
    input.addEventListener('keydown', this.#onKeydown);
    input.addEventListener('focus', this.#onFocus);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('submit', this.#onSubmit);
    panel.addEventListener('mousedown', this.#onPanelMouseDown);
    panel.addEventListener('pointermove', this.#onPointerMove);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  disconnectedCallback() {
    this.#cancelPending();
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('keydown', this.#onKeydown);
    this.#input?.removeEventListener('focus', this.#onFocus);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('submit', this.#onSubmit);
    this.#panel?.removeEventListener('mousedown', this.#onPanelMouseDown);
    this.#panel?.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
  }

  /** Current query, trimmed and lowercased (also the cache key). */
  #key() {
    return this.#input ? this.#input.value.trim().toLowerCase() : '';
  }

  /** @param {string} q */
  #buildUrl(q) {
    const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
    const params = url.searchParams;
    params.set('q', q);
    params.set('resources[type]', 'query,product');
    params.set('resources[limit]', '6');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('section_id', this.dataset.section || 'bb-predictive-search');
    return url;
  }

  #onInput = () => {
    const q = this.#input ? this.#input.value.trim() : '';
    this.#cancelPending();

    if (q.length < MIN_CHARS) {
      this.#reset();
      return;
    }

    const key = q.toLowerCase();
    const cached = this.#cache.get(key);
    if (cached !== undefined) {
      this.#render(cached, key);
      return;
    }

    this.#panel?.setAttribute('aria-busy', 'true');
    this.#timer = window.setTimeout(() => {
      this.#timer = 0;
      this.#fetch(q, key);
    }, DEBOUNCE_MS);
  };

  /** @param {string} q @param {string} key */
  async #fetch(q, key) {
    const controller = new AbortController();
    this.#abort = controller;
    this.#panel?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.#buildUrl(q), {
        signal: controller.signal,
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Predictive search failed with status ${response.status}`);
      const html = await response.text();
      this.#remember(key, html);

      // Race guard: the field may have changed or lost focus while the request was in flight
      if (this.#key() !== key || document.activeElement !== this.#input) return;
      this.#render(html, key);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[bb-search-predictive]', error);
      if (this.#abort === controller) this.#close();
    } finally {
      if (this.#abort === controller) {
        this.#abort = null;
        this.#panel?.removeAttribute('aria-busy');
      }
    }
  }

  /** @param {string} key @param {string} html */
  #remember(key, html) {
    this.#cache.delete(key);
    this.#cache.set(key, html);
    if (this.#cache.size > CACHE_LIMIT) {
      const oldest = this.#cache.keys().next().value;
      if (oldest !== undefined) this.#cache.delete(oldest);
    }
  }

  /** @param {string} html @param {string} key */
  #render(html, key) {
    const panel = this.#panel;
    if (!panel) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const root = doc.querySelector('[data-bb-ps-root]') ?? doc.querySelector('.shopify-section > *');
    if (!(root instanceof HTMLElement) || !root.querySelector('[role="option"]')) {
      this.#close();
      return;
    }

    panel.replaceChildren(document.importNode(root, true));
    panel.removeAttribute('aria-busy');
    this.#renderedKey = key;
    this.#setActive(-1);
    this.#show();
    this.#announce(root);
  }

  /** Short polite summary for screen readers, e.g. "6 productos y 3 sugerencias". */
  #announce(root) {
    if (!this.#status) return;
    const products = Number(root.dataset.bbPsProducts) || 0;
    const queries = Number(root.dataset.bbPsQueries) || 0;
    const parts = [];
    if (products > 0) parts.push(`${products} ${products === 1 ? 'producto' : 'productos'}`);
    if (queries > 0) parts.push(`${queries} ${queries === 1 ? 'sugerencia' : 'sugerencias'}`);
    this.#status.textContent = parts.length > 0 ? parts.join(' y ') : 'Sin sugerencias';
  }

  #show() {
    if (!this.#panel || !this.#input) return;
    this.#panel.hidden = false;
    this.#input.setAttribute('aria-expanded', 'true');
    this.#open = true;
  }

  #close() {
    if (!this.#panel || !this.#input) return;
    this.#setActive(-1);
    this.#panel.hidden = true;
    this.#input.setAttribute('aria-expanded', 'false');
    this.#open = false;
  }

  /** Close and drop the content (query too short to suggest anything). */
  #reset() {
    this.#close();
    this.#panel?.replaceChildren();
    this.#panel?.removeAttribute('aria-busy');
    this.#renderedKey = '';
    if (this.#status) this.#status.textContent = '';
  }

  /** Cancel the debounce timer and any in-flight request. */
  #cancelPending() {
    if (this.#timer) {
      window.clearTimeout(this.#timer);
      this.#timer = 0;
    }
    if (this.#abort) {
      this.#abort.abort();
      this.#abort = null;
    }
    this.#panel?.removeAttribute('aria-busy');
  }

  /** Close without letting a late response reopen the panel. */
  #dismiss() {
    this.#cancelPending();
    this.#close();
  }

  /**
   * Reopen with the content that belongs to the current query, if there is any.
   * @param {string} key
   * @returns {boolean}
   */
  #restore(key) {
    if (this.#renderedKey === key && this.#panel?.firstElementChild) {
      this.#show();
      return true;
    }
    const cached = this.#cache.get(key);
    if (cached !== undefined) {
      this.#render(cached, key);
      return this.#open;
    }
    return false;
  }

  /** @returns {HTMLElement[]} */
  #options() {
    return this.#panel ? Array.from(this.#panel.querySelectorAll('[role="option"]')).filter((el) => el instanceof HTMLElement) : [];
  }

  /** @param {number} index -1 clears the active option */
  #setActive(index) {
    const options = this.#options();
    options.forEach((option, i) => option.setAttribute('aria-selected', i === index ? 'true' : 'false'));

    const active = index >= 0 ? options[index] : undefined;
    this.#activeIndex = active ? index : -1;
    if (active && this.#input) {
      this.#input.setAttribute('aria-activedescendant', active.id);
    } else {
      this.#input?.removeAttribute('aria-activedescendant');
    }
    return active;
  }

  /** @param {number} delta 1 or -1 */
  #move(delta) {
    const count = this.#options().length;
    if (count === 0) return;

    let next;
    if (this.#activeIndex === -1) next = delta > 0 ? 0 : count - 1;
    else next = (this.#activeIndex + delta + count) % count;

    const active = this.#setActive(next);
    if (active) this.#scrollIntoPanel(active);
  }

  /**
   * Scroll only the panel (never an ancestor: this theme scrolls inside .page-wrapper).
   * @param {HTMLElement} option
   */
  #scrollIntoPanel(option) {
    const panel = this.#panel;
    if (!panel) return;
    const p = panel.getBoundingClientRect();
    const o = option.getBoundingClientRect();
    if (o.top < p.top) panel.scrollTop -= p.top - o.top;
    else if (o.bottom > p.bottom) panel.scrollTop += o.bottom - p.bottom;
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.isComposing) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (!this.#open) {
          const key = this.#key();
          if (key.length >= MIN_CHARS && this.#restore(key)) event.preventDefault();
          return;
        }
        event.preventDefault();
        this.#move(event.key === 'ArrowDown' ? 1 : -1);
        break;
      }
      case 'Enter': {
        if (!this.#open || this.#activeIndex < 0) return; // normal form submit
        const option = this.#options()[this.#activeIndex];
        if (option instanceof HTMLAnchorElement && option.href) {
          event.preventDefault();
          window.location.href = option.href;
        }
        break;
      }
      case 'Escape': {
        if (!this.#open) return;
        // type=search would otherwise clear the field on Escape
        event.preventDefault();
        this.#dismiss();
        break;
      }
      case 'Tab':
        this.#dismiss();
        break;
      default:
    }
  };

  #onFocus = () => {
    if (this.#open) return;
    const key = this.#key();
    if (key.length >= MIN_CHARS) this.#restore(key);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    // A null relatedTarget is the window losing focus: keep the panel as it is
    if (next instanceof Node && !this.contains(next)) this.#dismiss();
  };

  #onSubmit = () => {
    this.#dismiss();
  };

  /** Keep DOM focus in the input when the panel is pressed. @param {MouseEvent} event */
  #onPanelMouseDown = (event) => {
    event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const option = target.closest('[role="option"]');
    if (!(option instanceof HTMLElement)) return;
    const index = this.#options().indexOf(option);
    if (index >= 0 && index !== this.#activeIndex) this.#setActive(index);
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (!this.#open && !this.#timer && !this.#abort) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#dismiss();
  };
}

if (!customElements.get('bb-search-predictive')) {
  customElements.define('bb-search-predictive', BBSearchPredictive);
}
